import { apiClient } from './apiClient';
import type { PaginatedResponse } from './apiClient';
import type {
  FotoTurnoResponse,
  AbastecimientoTurno,
  ConciliacionDetalle,
  ControlCombustibleResumen,
  AbastecimientoCreateRequest,
  ConciliacionFiltros,
  TipoFotoTurno,
} from '../types/combustible';

export interface SubirEvidenciaParams {
  tipo: TipoFotoTurno;
  fotoEvidencia: File;
  fotoSelfie: File;
  horometroDeclarado?: number;
  nivelDeclarado?: number;
}

export interface RegularizarTurnoParams {
  nivel_inicio?: number;
  nivel_fin?: number;
  litros_entregados?: number;
  observaciones?: string;
}

export interface EditarEvidenciaParams {
  nivelDeclarado?: number;
  horometroDeclarado?: number;
  fotoEvidencia?: File;
  fotoSelfie?: File;
}

export interface AgregarEvidenciaParams {
  tipo: TipoFotoTurno;
  fotoEvidencia: File;
  fotoSelfie?: File;
  nivelDeclarado?: number;
  horometroDeclarado?: number;
}

class CombustibleService {
  async subirEvidencia(reporteId: number, params: SubirEvidenciaParams): Promise<FotoTurnoResponse> {
    const formData = new FormData();
    formData.append('tipo', params.tipo);
    formData.append('foto_evidencia', params.fotoEvidencia);
    formData.append('foto_selfie', params.fotoSelfie);
    if (params.horometroDeclarado !== undefined) {
      formData.append('horometro_declarado', String(params.horometroDeclarado));
    }
    if (params.nivelDeclarado !== undefined) {
      formData.append('nivel_declarado', String(params.nivelDeclarado));
    }

    const response = await apiClient.post<FotoTurnoResponse>(
      `/combustible/evidencia/${reporteId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async registrarAbastecimiento(reporteId: number, data: AbastecimientoCreateRequest): Promise<AbastecimientoTurno> {
    const response = await apiClient.post<AbastecimientoTurno>(`/combustible/abastecimiento/${reporteId}`, data);
    return response.data;
  }

  async getConciliacion(filtros: ConciliacionFiltros): Promise<PaginatedResponse<ControlCombustibleResumen>> {
    const response = await apiClient.get<PaginatedResponse<ControlCombustibleResumen>>('/combustible/conciliacion', {
      params: {
        page: filtros.page ?? 1,
        per_page: filtros.per_page ?? 20,
        fecha_inicio: filtros.fecha_inicio,
        fecha_fin: filtros.fecha_fin,
        maquina_id: filtros.maquina_id,
      },
    });
    return response.data;
  }

  async getConciliacionDetalle(reporteId: number): Promise<ConciliacionDetalle> {
    const response = await apiClient.get<ConciliacionDetalle>(`/combustible/conciliacion/${reporteId}`);
    return response.data;
  }

  /** Regulariza (admin/supervisor) los niveles y litros de un turno. */
  async regularizarTurno(reporteId: number, params: RegularizarTurnoParams): Promise<ConciliacionDetalle> {
    const response = await apiClient.put<ConciliacionDetalle>(`/combustible/regularizar/${reporteId}`, params);
    return response.data;
  }

  /** Edita una evidencia existente: nivel/horómetro y, opcionalmente, reemplaza fotos. */
  async editarEvidencia(fotoId: number, params: EditarEvidenciaParams): Promise<FotoTurnoResponse> {
    const formData = new FormData();
    if (params.nivelDeclarado !== undefined) {
      formData.append('nivel_declarado', String(params.nivelDeclarado));
    }
    if (params.horometroDeclarado !== undefined) {
      formData.append('horometro_declarado', String(params.horometroDeclarado));
    }
    if (params.fotoEvidencia) {
      formData.append('foto_evidencia', params.fotoEvidencia);
    }
    if (params.fotoSelfie) {
      formData.append('foto_selfie', params.fotoSelfie);
    }

    const response = await apiClient.put<FotoTurnoResponse>(
      `/combustible/evidencia/${fotoId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  /** Agrega una evidencia (regularización) a un turno que no la tiene; selfie opcional. */
  async agregarEvidencia(reporteId: number, params: AgregarEvidenciaParams): Promise<FotoTurnoResponse> {
    const formData = new FormData();
    formData.append('tipo', params.tipo);
    formData.append('foto_evidencia', params.fotoEvidencia);
    if (params.fotoSelfie) {
      formData.append('foto_selfie', params.fotoSelfie);
    }
    if (params.nivelDeclarado !== undefined) {
      formData.append('nivel_declarado', String(params.nivelDeclarado));
    }
    if (params.horometroDeclarado !== undefined) {
      formData.append('horometro_declarado', String(params.horometroDeclarado));
    }

    const response = await apiClient.post<FotoTurnoResponse>(
      `/combustible/regularizar/${reporteId}/evidencia`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async actualizarConsumoEspecifico(maquinaId: number, consumoEspecifico: number): Promise<{ id: number; codigo: string; consumo_especifico: number }> {
    const response = await apiClient.put(`/combustible/maquina/${maquinaId}/consumo-especifico`, {
      consumo_especifico: consumoEspecifico,
    });
    return response.data;
  }
}

export const combustibleService = new CombustibleService();
export default combustibleService;
